import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banknote, Download, Loader2, TrendingUp, TrendingDown, Wallet, Receipt, Users, Building2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function presetRange(preset) {
  const now = new Date();
  const today = iso(now);
  if (preset === "today") return { from: today, to: today };
  if (preset === "week") { const s = new Date(now); s.setDate(now.getDate() - 6); return { from: iso(s), to: today }; }
  if (preset === "month") { const s = new Date(now.getFullYear(), now.getMonth(), 1); return { from: iso(s), to: today }; }
  return { from: "", to: "" };
}

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TILES = [
  { key: "gross_collected", label: "Gross collected", icon: TrendingUp, color: "#144F75" },
  { key: "refunds", label: "Refunds", icon: TrendingDown, color: "#B45151" },
  { key: "net_collected", label: "Net collected", icon: Banknote, color: "#5C8A72" },
  { key: "deposits_collected", label: "Deposits collected", icon: Wallet, color: "#B5794E" },
  { key: "outstanding_balances", label: "Outstanding balances", icon: Receipt, color: "#B5794E" },
  { key: "tips", label: "Tips", icon: Wallet, color: "#5C8A72" },
];

const PAYOUT_TILES = [
  { key: "enhancer_share", label: "Enhancer earnings", icon: Users, color: "#144F75" },
  { key: "ecosystem_share", label: "Ecosystem share", icon: Building2, color: "#8FA89B" },
  { key: "partner_share", label: "Partner / center share", icon: Building2, color: "#B5794E" },
];

export default function ManagerReconciliationPanel() {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["mgr-reconciliation", range.from, range.to],
    queryFn: () => api.get(`/manager/reconciliation?dfrom=${range.from}&dto=${range.to}`).then((r) => r.data),
  });

  const applyPreset = (p) => { setPreset(p); setRange(presetRange(p)); };
  const setCustom = (field, val) => { setPreset("custom"); setRange((r) => ({ ...r, [field]: val })); };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/manager/reconciliation.csv?dfrom=${range.from}&dto=${range.to}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "eyw-center-reconciliation.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Reconciliation exported");
    } catch { toast.error("We couldn't prepare that export. Please try again."); } finally { setDownloading(false); }
  };

  const t = data?.totals || {};
  const methods = data?.by_payment_method || {};
  const statuses = data?.appointments_by_status || {};

  return (
    <div data-testid="mgr-reconciliation-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Banknote size={22} className="text-[#144F75]" /> Reconciliation</h2>
          <p className="text-sm text-[#4A5D6B] mt-1">Money collected at {data?.center_name || "your center"} — appointments &amp; POS, refunds, tips, deposits, outstanding balances, and payment methods. Export matches these totals.</p>
        </div>
        <button onClick={exportCsv} disabled={downloading} data-testid="mgr-recon-export" className="eyw-btn-ghost !py-2 text-sm disabled:opacity-60">
          {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-5">
        {[["today", "Today"], ["week", "Last 7 days"], ["month", "This month"], ["all", "All time"]].map(([k, lbl]) => (
          <button key={k} onClick={() => applyPreset(k)} data-testid={`mgr-recon-preset-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm ${preset === k ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>{lbl}</button>
        ))}
        <span className="mx-1 text-[#D5CEC2]">|</span>
        <input type="date" value={range.from} onChange={(e) => setCustom("from", e.target.value)} data-testid="mgr-recon-from" className="eyw-input !py-1.5 text-sm" />
        <span className="text-[#8FA89B] text-sm">to</span>
        <input type="date" value={range.to} onChange={(e) => setCustom("to", e.target.value)} data-testid="mgr-recon-to" className="eyw-input !py-1.5 text-sm" />
      </div>

      {isLoading ? (
        <div className="p-10 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /> Loading…</div>
      ) : data?.no_center ? (
        <div className="eyw-card p-8 text-center text-[#4A5D6B]" data-testid="mgr-recon-no-center">No center assigned yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {TILES.map((tile) => (
              <div key={tile.key} className="eyw-card p-4" data-testid={`mgr-recon-tile-${tile.key}`}>
                <div className="flex items-center gap-2 text-xs text-[#8FA89B]"><tile.icon size={14} style={{ color: tile.color }} /> {tile.label}</div>
                <div className="text-2xl font-serif mt-1" style={{ color: tile.color }}>{money(t[tile.key])}</div>
              </div>
            ))}
          </div>

          <div className="mb-6">
            <h3 className="font-serif text-lg text-[#1A242B] mb-3">Payout summary — who this money is owed to</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {PAYOUT_TILES.map((tile) => (
                <div key={tile.key} className="eyw-card p-4" data-testid={`mgr-recon-payout-${tile.key}`}>
                  <div className="flex items-center gap-2 text-xs text-[#8FA89B]"><tile.icon size={14} style={{ color: tile.color }} /> {tile.label}</div>
                  <div className="text-2xl font-serif mt-1" style={{ color: tile.color }}>{money(t[tile.key])}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#8FA89B] mt-2">Splits from your center's revenue ledger for the period (gross before refunds). Enhancer earnings + ecosystem + partner shares add up to gross collected.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="eyw-card p-5" data-testid="mgr-recon-methods">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Payments by method</h3>
              <div className="space-y-2">
                {Object.entries(methods).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm border-b border-[#F1EEE8] pb-1.5">
                    <span className="text-[#4A5D6B] capitalize">{k.replace("_", " ")}</span>
                    <span className="font-semibold text-[#1A242B]">{money(v)}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#8FA89B] mt-3">POS tender totals at your center (cash, card, gift card, Stripe).</p>
            </div>

            <div className="eyw-card p-5" data-testid="mgr-recon-statuses">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Appointments by status</h3>
              <div className="space-y-2">
                {Object.entries(statuses).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm border-b border-[#F1EEE8] pb-1.5">
                    <span className="text-[#4A5D6B] capitalize">{k.replace("_", " ")}</span>
                    <span className="font-semibold text-[#1A242B]">{v}</span>
                  </div>
                ))}
                {Object.keys(statuses).length === 0 && <p className="text-sm text-[#8FA89B]">No appointments in range.</p>}
              </div>
              <p className="text-[11px] text-[#8FA89B] mt-3">{t.appointments || 0} appointments · {t.pos_transactions || 0} POS sales at your center.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
